import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { CustomDropdownContainer, FormFieldLabel, PageHeader } from "CommonComponent";
import { hideCommonLoader, showCommonLoader } from "actions/commonActions";
import { useObjectViewedId } from "hooks/useObjectViewedId";
import {
  FREQUENCY_OPTIONS,
  MONTH_OPTIONS,
  PROBATION_UNIT_OPTIONS,
  STRATEGY_OPTIONS,
  clearTestPafs,
  deadlineOffsetSelectOptions,
  emptyDeadlineOffsets,
  emptyTestingIntervals,
  fetchGlobalSettings,
  generateCurrentTestPafs,
  getErrorMessage,
  isSupportedOffsetMinutes,
  normalizeDeadlineOffsets,
  previewClearTestPafs,
  TESTING_ACTION_OPTIONS,
  TESTING_INTERVAL_OPTIONS,
  testingIntervalsFromApi,
  updateGlobalSettings,
  validateDeadlineOffsetsClient,
} from "../pafEngineApi";
import "../PafEngine.css";

const notify = (message, type = "general_error") => ({
  type: "SHOW_SNACKBAR",
  snackbar_notification: message,
  snackbar_notification_type: type,
});

const optionByValue = (options, value) =>
  options.find((o) => o.value === value) || null;

const testPafLabel = (count) => `${count} test PAF${Number(count) === 1 ? "" : "s"}`;

/** Avoid clipping by .content-area overflow when menus open near the page bottom. */
const selectMenuProps = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
  classNames: {
    menu: () => "paf-settings-select-menu",
  },
  styles: {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  },
};

const DeadlineOffsetSelect = ({
  inputId,
  label,
  value,
  currentLabel,
  error,
  onChange,
}) => {
  const options = deadlineOffsetSelectOptions(value, currentLabel);
  const selected = optionByValue(options, value);
  const unsupported =
    value !== "" && value !== null && value !== undefined && !isSupportedOffsetMinutes(value);

  return (
    <>
      <FormFieldLabel>{label}</FormFieldLabel>
      <Select
        inputId={inputId}
        className="multi-select"
        classNamePrefix="react-select"
        aria-label={label}
        aria-invalid={error || unsupported ? true : undefined}
        options={options}
        value={selected}
        placeholder="Select offset"
        onChange={(opt) => onChange(opt?.value ?? "")}
        components={{ ValueContainer: CustomDropdownContainer }}
        {...selectMenuProps}
        styles={{
          ...selectMenuProps.styles,
          control: (base, state) => ({
            ...base,
            minHeight: 38,
            borderColor: error || unsupported ? "#dc3545" : base.borderColor,
            boxShadow: error || unsupported ? "none" : state.isFocused ? base.boxShadow : "none",
            "&:hover": {
              borderColor: error || unsupported ? "#dc3545" : base.borderColor,
            },
          }),
        }}
      />
      {error ? <div className="paf-engine-deadline-error">{error}</div> : null}
      {!error && unsupported ? (
        <div className="paf-engine-deadline-error">
          This existing offset is not one of the supported options. Choose a listed duration
          before saving.
        </div>
      ) : null}
    </>
  );
};

const PafEngineGlobalSettings = () => {
  const dispatch = useDispatch();
  const objectViewedId = useObjectViewedId();
  const [form, setForm] = useState({
    auto_generate_enabled: true,
    probation_period: 3,
    probation_unit: "month",
    frequency_strategies: {
      monthly: "company_schedule",
      quarterly: "company_schedule",
      yearly: "company_schedule",
    },
    deadline_offsets: emptyDeadlineOffsets(),
    scheduler_testing_enabled: false,
    testing_intervals: emptyTestingIntervals(),
    company_monthly_day: 1,
    quarterly_start_month: "January",
    yearly_generation_month: "January",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [testingBusy, setTestingBusy] = useState(false);
  const [clearCount, setClearCount] = useState(null);

  const load = useCallback(async () => {
    dispatch(showCommonLoader());
    try {
      const data = await fetchGlobalSettings(objectViewedId);
      setForm((prev) => ({
        ...prev,
        ...data,
        frequency_strategies: {
          monthly: data?.frequency_strategies?.monthly || "company_schedule",
          quarterly: data?.frequency_strategies?.quarterly || "company_schedule",
          yearly: data?.frequency_strategies?.yearly || "company_schedule",
        },
        deadline_offsets: normalizeDeadlineOffsets(data?.deadline_offsets),
        scheduler_testing_enabled: !!data?.scheduler_testing?.enabled,
        testing_intervals: testingIntervalsFromApi(data?.scheduler_testing),
      }));
      setFieldErrors({});
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      dispatch(hideCommonLoader());
    }
  }, [dispatch, objectViewedId]);

  useEffect(() => {
    load();
  }, [load]);

  const setDeadlineOffset = (frequency, key, rawValue) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[`deadline_offsets.${frequency}.${key}`];
      return next;
    });
    setForm((prev) => ({
      ...prev,
      deadline_offsets: {
        ...prev.deadline_offsets,
        [frequency]: {
          ...prev.deadline_offsets?.[frequency],
          [key]: rawValue === "" || rawValue === null || rawValue === undefined
            ? ""
            : rawValue,
        },
      },
    }));
  };

  const save = async () => {
    const deadlinePayload = normalizeDeadlineOffsets(form.deadline_offsets);
    const clientErrors = validateDeadlineOffsetsClient(deadlinePayload);
    const testingIntervals = form.testing_intervals || emptyTestingIntervals();
    const testingIntervalPayload = { ...emptyTestingIntervals(), ...testingIntervals };
    const testingIntervalMissing = [...FREQUENCY_OPTIONS, ...TESTING_ACTION_OPTIONS].some(
      (item) => !TESTING_INTERVAL_OPTIONS.some((option) => option.value === testingIntervalPayload[item.value])
    );
    if (form.scheduler_testing_enabled && testingIntervalMissing) {
      dispatch(notify("Select a testing interval for each generation frequency, email reminders, auto-approve, and auto employee sign."));
      return;
    }
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      dispatch(
        notify(
          Object.values(clientErrors)[0] ||
            "Please complete all deadline offset fields before saving."
        )
      );
      return;
    }

    setSaving(true);
    dispatch(showCommonLoader());
    try {
      const data = await updateGlobalSettings(
        {
          auto_generate_enabled: !!form.auto_generate_enabled,
          probation_period: Number(form.probation_period),
          probation_unit: form.probation_unit,
          frequency_strategies: form.frequency_strategies,
          generation_strategy: form.frequency_strategies?.monthly || "company_schedule",
          company_monthly_day: Number(form.company_monthly_day),
          quarterly_start_month: form.quarterly_start_month,
          yearly_generation_month: form.yearly_generation_month,
          deadline_offsets: deadlinePayload,
          scheduler_testing_enabled: !!form.scheduler_testing_enabled,
          ...(testingIntervalMissing
            ? {}
            : { scheduler_testing_intervals: testingIntervalPayload }),
        },
        objectViewedId
      );
      setForm((prev) => ({
        ...prev,
        ...data,
        frequency_strategies: {
          monthly: data?.frequency_strategies?.monthly || prev.frequency_strategies.monthly,
          quarterly: data?.frequency_strategies?.quarterly || prev.frequency_strategies.quarterly,
          yearly: data?.frequency_strategies?.yearly || prev.frequency_strategies.yearly,
        },
        deadline_offsets: normalizeDeadlineOffsets(data?.deadline_offsets),
        scheduler_testing_enabled: !!data?.scheduler_testing?.enabled,
        testing_intervals: testingIntervalsFromApi(data?.scheduler_testing),
      }));
      setFieldErrors({});
      dispatch(notify("Global settings saved.", "success"));
    } catch (error) {
      const apiErrors = error?.response?.data?.errors;
      if (apiErrors && typeof apiErrors === "object") {
        const flattened = {};
        Object.entries(apiErrors).forEach(([key, messages]) => {
          flattened[key] = Array.isArray(messages) ? messages[0] : String(messages);
        });
        setFieldErrors(flattened);
      }
      dispatch(notify(getErrorMessage(error)));
    } finally {
      setSaving(false);
      dispatch(hideCommonLoader());
    }
  };

  const deadlineError = (frequency, key) =>
    fieldErrors[`deadline_offsets.${frequency}.${key}`] ||
    fieldErrors[`deadline_offsets.${frequency}`];

  const runTestingAction = async (action) => {
    setTestingBusy(true);
    dispatch(showCommonLoader());
    try {
      await action();
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      setTestingBusy(false);
      dispatch(hideCommonLoader());
    }
  };

  const generateCurrent = async () => {
    const data = await generateCurrentTestPafs(objectViewedId);
    const generated = Object.values(data?.frequencies || {}).reduce(
      (sum, row) => sum + Number(row?.generated || 0),
      0
    );
    dispatch(
      notify(
        `Current period generation finished. Generated ${generated} PAFs. Batch ${data?.batch_id || ""}.`,
        "success"
      )
    );
    await load();
  };

  const clearTests = async () => {
    setTestingBusy(true);
    dispatch(showCommonLoader());
    try {
      const preview = await previewClearTestPafs(objectViewedId);
      const count = Number(preview?.test_pafs || 0);
      dispatch(hideCommonLoader());
      if (count === 0) {
        dispatch(notify(preview?.message || "No test PAFs found."));
        return;
      }
      setClearCount(count);
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      setTestingBusy(false);
      dispatch(hideCommonLoader());
    }
  };

  const confirmClearTests = async () => {
    const count = clearCount;
    setClearCount(null);
    setTestingBusy(true);
    dispatch(showCommonLoader());
    try {
      const result = await clearTestPafs(objectViewedId);
      const deleted = Number(result?.deleted ?? count ?? 0);
      dispatch(
        notify(
          result?.message ||
            `${testPafLabel(deleted)} ${deleted === 1 ? "was" : "were"} permanently deleted.`,
          "success"
        )
      );
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      setTestingBusy(false);
      dispatch(hideCommonLoader());
    }
  };

  return (
    <div className="paf-settings-page">
      <PageHeader title="Global PAF Settings">
        <Button className="n-u-btn" disabled={saving} onClick={save}>
          Save
        </Button>
      </PageHeader>

      <div className="main-content-div page-card">
        <div className="paf-engine-section-title">General</div>
        <Form.Check
          type="switch"
          id="auto-generate"
          label="Auto Generate PAFs"
          checked={!!form.auto_generate_enabled}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, auto_generate_enabled: e.target.checked }))
          }
        />

        <div className="paf-engine-section-title">Probation</div>
        <Row className="g-3 mb-2">
          <Col md={3}>
            <FormFieldLabel>Probation Period</FormFieldLabel>
            <Form.Control
              type="number"
              min={1}
              value={form.probation_period}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, probation_period: e.target.value }))
              }
            />
          </Col>
          <Col md={3}>
            <FormFieldLabel>Unit</FormFieldLabel>
            <Select
              className="multi-select"
              classNamePrefix="react-select"
              options={PROBATION_UNIT_OPTIONS}
              value={optionByValue(PROBATION_UNIT_OPTIONS, form.probation_unit)}
              onChange={(opt) =>
                setForm((prev) => ({ ...prev, probation_unit: opt?.value || "month" }))
              }
              components={{ ValueContainer: CustomDropdownContainer }}
              {...selectMenuProps}
            />
          </Col>
        </Row>

        <div className="paf-engine-section-title">Generation Strategy by Frequency</div>
        <Row className="g-3 mb-2">
          {FREQUENCY_OPTIONS.map((freq) => (
            <Col md={4} key={freq.value}>
              <FormFieldLabel>{freq.label}</FormFieldLabel>
              <Select
                className="multi-select"
                classNamePrefix="react-select"
                options={STRATEGY_OPTIONS}
                value={optionByValue(
                  STRATEGY_OPTIONS,
                  form.frequency_strategies?.[freq.value]
                )}
                onChange={(opt) =>
                  setForm((prev) => ({
                    ...prev,
                    frequency_strategies: {
                      ...prev.frequency_strategies,
                      [freq.value]: opt?.value || "company_schedule",
                    },
                  }))
                }
                components={{ ValueContainer: CustomDropdownContainer }}
                {...selectMenuProps}
              />
            </Col>
          ))}
        </Row>

        <div className="paf-engine-section-title">Company Schedule</div>
        <Row className="g-3">
          <Col md={3}>
            <FormFieldLabel>Monthly Generate On (Day)</FormFieldLabel>
            <Form.Control
              type="number"
              min={1}
              max={28}
              value={form.company_monthly_day}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, company_monthly_day: e.target.value }))
              }
            />
          </Col>
          <Col md={3}>
            <FormFieldLabel>Quarterly Start Month</FormFieldLabel>
            <Select
              className="multi-select"
              classNamePrefix="react-select"
              options={MONTH_OPTIONS}
              value={optionByValue(MONTH_OPTIONS, form.quarterly_start_month)}
              onChange={(opt) =>
                setForm((prev) => ({
                  ...prev,
                  quarterly_start_month: opt?.value || "January",
                }))
              }
              components={{ ValueContainer: CustomDropdownContainer }}
              {...selectMenuProps}
            />
          </Col>
          <Col md={3}>
            <FormFieldLabel>Yearly Generation Month</FormFieldLabel>
            <Select
              className="multi-select"
              classNamePrefix="react-select"
              options={MONTH_OPTIONS}
              value={optionByValue(MONTH_OPTIONS, form.yearly_generation_month)}
              onChange={(opt) =>
                setForm((prev) => ({
                  ...prev,
                  yearly_generation_month: opt?.value || "January",
                }))
              }
              components={{ ValueContainer: CustomDropdownContainer }}
              {...selectMenuProps}
            />
          </Col>
        </Row>

        <div className="paf-engine-section-title">Deadline Offsets</div>
        <p className="paf-engine-tagline">
          Deadline is calculated from the actual PAF generation date and time. Offsets can
          be configured in minutes, hours, or days. Changing these values does not
          recalculate deadlines on existing PAFs. New PAFs will not generate until all
          required offsets are configured.
        </p>
        <p className="paf-engine-tagline">
          One-Level action deadline and Two-Level scoring deadline share the same stored
          scoring offset. Two-Level also requires a separate approval offset.
        </p>

        {FREQUENCY_OPTIONS.map((freq) => {
          const bucket = form.deadline_offsets?.[freq.value] || {
            scoring_offset_days: "",
            approval_offset_days: "",
          };
          const scoringErr = deadlineError(freq.value, "scoring_offset_days");
          const approvalErr = deadlineError(freq.value, "approval_offset_days");
          const scoringValue =
            bucket.scoring_offset_days === null || bucket.scoring_offset_days === undefined
              ? ""
              : bucket.scoring_offset_days;
          const approvalValue =
            bucket.approval_offset_days === null || bucket.approval_offset_days === undefined
              ? ""
              : bucket.approval_offset_days;

          return (
            <div className="paf-engine-deadline-freq" key={freq.value}>
              <div className="paf-engine-deadline-freq-title">{freq.label}</div>

              <div className="paf-engine-deadline-workflow">
                <div className="paf-engine-deadline-workflow-title">One-Level</div>
                <p className="paf-engine-deadline-help">
                  Same person scores and approves. Action deadline equals scoring and
                  approval.
                </p>
                <Row className="g-3">
                  <Col md={4}>
                    <DeadlineOffsetSelect
                      inputId={`deadline-${freq.value}-one-level-action`}
                      label="Action Deadline"
                      value={scoringValue}
                      currentLabel={bucket.scoring_offset_label}
                      error={scoringErr}
                      onChange={(next) =>
                        setDeadlineOffset(freq.value, "scoring_offset_days", next)
                      }
                    />
                  </Col>
                </Row>
              </div>

              <div className="paf-engine-deadline-workflow">
                <div className="paf-engine-deadline-workflow-title">Two-Level</div>
                <p className="paf-engine-deadline-help">
                  Scorer and approver are different reporting levels. Scoring and approval
                  deadlines may differ.
                </p>
                <Row className="g-3">
                  <Col md={4}>
                    <DeadlineOffsetSelect
                      inputId={`deadline-${freq.value}-two-level-scoring`}
                      label="Scoring Deadline"
                      value={scoringValue}
                      currentLabel={bucket.scoring_offset_label}
                      error={scoringErr}
                      onChange={(next) =>
                        setDeadlineOffset(freq.value, "scoring_offset_days", next)
                      }
                    />
                  </Col>
                  <Col md={4}>
                    <DeadlineOffsetSelect
                      inputId={`deadline-${freq.value}-two-level-approval`}
                      label="Approval Deadline"
                      value={approvalValue}
                      currentLabel={bucket.approval_offset_label}
                      error={approvalErr}
                      onChange={(next) =>
                        setDeadlineOffset(freq.value, "approval_offset_days", next)
                      }
                    />
                  </Col>
                </Row>
              </div>
            </div>
          );
        })}

        <div className="paf-engine-section-title">PAF Scheduler Testing</div>
        <p className="paf-engine-testing-warning">
          Testing Mode accelerates PAF generation, email reminders, auto-approve, and auto
          employee sign for testing. Existing frequency, eligibility, hierarchy, and
          duplicate-prevention rules still apply. Reminder, approval, and employee-sign
          testing runs affect test PAFs only.
        </p>
        {!form.scheduler_testing_enabled ? (
          <p className="paf-engine-tagline">
            Testing Mode is off. Monthly, quarterly, and yearly generation keep their normal
            schedule.
          </p>
        ) : null}
        <Form.Check
          type="switch"
          id="scheduler-testing-mode"
          label={form.scheduler_testing_enabled ? "Testing Mode ON" : "Testing Mode OFF"}
          checked={!!form.scheduler_testing_enabled}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, scheduler_testing_enabled: e.target.checked }))
          }
        />
        <Row className="g-3 mt-1">
          {FREQUENCY_OPTIONS.map((freq) => (
            <Col md={4} key={`test-${freq.value}`}>
              <FormFieldLabel>{freq.label} Testing Interval</FormFieldLabel>
              <Select
                inputId={`testing-interval-${freq.value}`}
                className="multi-select"
                classNamePrefix="react-select"
                aria-label={`${freq.label} Testing Interval`}
                options={TESTING_INTERVAL_OPTIONS}
                value={optionByValue(TESTING_INTERVAL_OPTIONS, form.testing_intervals?.[freq.value])}
                placeholder="Select interval"
                onChange={(opt) =>
                  setForm((prev) => ({
                    ...prev,
                    testing_intervals: {
                      ...emptyTestingIntervals(),
                      ...prev.testing_intervals,
                      [freq.value]: opt?.value ?? "",
                    },
                  }))
                }
                components={{ ValueContainer: CustomDropdownContainer }}
                {...selectMenuProps}
              />
              {form.scheduler_testing?.intervals?.[freq.value]?.last_run_at ? (
                <p className="paf-engine-deadline-help">
                  Last run: {form.scheduler_testing.intervals[freq.value].last_run_at}
                </p>
              ) : null}
            </Col>
          ))}
        </Row>
        <Row className="g-3 mt-1">
          {TESTING_ACTION_OPTIONS.map((action) => (
            <Col md={4} key={`test-${action.value}`}>
              <FormFieldLabel>{action.label} Testing Interval</FormFieldLabel>
              <Select
                inputId={`testing-interval-${action.value}`}
                className="multi-select"
                classNamePrefix="react-select"
                aria-label={`${action.label} Testing Interval`}
                options={TESTING_INTERVAL_OPTIONS}
                value={optionByValue(TESTING_INTERVAL_OPTIONS, form.testing_intervals?.[action.value])}
                placeholder="Select interval"
                onChange={(opt) =>
                  setForm((prev) => ({
                    ...prev,
                    testing_intervals: {
                      ...emptyTestingIntervals(),
                      ...prev.testing_intervals,
                      [action.value]: opt?.value ?? "",
                    },
                  }))
                }
                components={{ ValueContainer: CustomDropdownContainer }}
                {...selectMenuProps}
              />
              {form.scheduler_testing?.intervals?.[action.value]?.last_run_at ? (
                <p className="paf-engine-deadline-help">
                  Last run: {form.scheduler_testing.intervals[action.value].last_run_at}
                </p>
              ) : null}
            </Col>
          ))}
        </Row>
        <div className="d-flex flex-wrap gap-2 mt-3">
          <Button
            className="n-u-btn"
            disabled={testingBusy}
            onClick={() => runTestingAction(generateCurrent)}
          >
            Generate Current Period PAFs
          </Button>
          <Button variant="outline-danger" disabled={testingBusy} onClick={clearTests}>
            Clear Test PAFs
          </Button>
        </div>
      </div>

      <Modal show={clearCount !== null} onHide={() => setClearCount(null)} centered>
        <Modal.Header className="heading-color" closeButton>
          <Modal.Title className="font-20 font-weight-s-bold heading-color">
            Clear Test PAFs
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {testPafLabel(clearCount)} will be permanently deleted. This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="c-b-btn" onClick={() => setClearCount(null)}>
            Cancel
          </button>
          <button type="button" className="delete-btn" disabled={testingBusy} onClick={confirmClearTests}>
            Delete
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PafEngineGlobalSettings;
